import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Input, Button, Link } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth } from '../../contexts/auth-context';
import { useRandomBackground } from '../../hooks/useRandomBackground';
import { useTheme } from '../../stores/theme-store';
import { ThemeSwitcher } from '../../components/ThemeSwitcher';
import { toast } from '../../stores/ui-store';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const history = useHistory();
  const backgroundImage = useRandomBackground();
  const { resolvedTheme } = useTheme();
  
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [captchaToken, setCaptchaToken] = React.useState<string>('');
  const captchaRef = React.useRef<string>('');
  const turnstileRef = React.useRef<any>(null);
  const isCaptchaEnabled = import.meta.env.VITE_ENABLE_CAPTCHA === 'true';

  // Redirect to dashboard if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      console.log('✅ User already authenticated, redirecting to dashboard...');
      history.replace('/dashboard');
    }
  }, [isAuthenticated, history]);

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const tokenToUse = captchaRef.current || captchaToken;
    console.log('🔐 Submitting with token:', tokenToUse?.slice(0, 50));
    
    if (isCaptchaEnabled && !tokenToUse) {
      toast.error(t('toastMessages.captchaRequired'));
      return;
    }
    
    setIsLoading(true);
    try {
      await login(email, password, tokenToUse);
      console.log('✅ Login successful, redirecting...');
      toast.success(t('toastMessages.loginSuccess'));
      
      // Navigate to dashboard
      history.push('/dashboard');
    } catch (err: any) {
      console.error('❌ Login error:', err);
      const errorMessage = err?.message || t('toastMessages.loginError');
      toast.error(errorMessage);
      
      // Reset captcha on error
      if (turnstileRef.current?.reset) {
        turnstileRef.current.reset();
      }
      captchaRef.current = '';
      setCaptchaToken('');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if form is valid and ready to submit
  const isFormValid = email && password && (isCaptchaEnabled ? captchaToken : true);

  return (
    <div className="flex items-center h-screen justify-center">
      <div 
        className="bg-content1 flex min-h-screen w-full items-center justify-start overflow-hidden p-2 sm:p-4 lg:p-8 relative"
        style={{
          backgroundImage: backgroundImage ? `url("${backgroundImage}")` : 'url("/images/login-background.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        {/* Logo - Top Right */}
        <div className="absolute top-10 right-10">
          <img src="/svg/astan-logo.svg" alt="آستان قدس رضوی" className="h-10" />
        </div>

        {/* Theme Switcher - Top Left */}
        <div className="absolute top-10 left-10">
          <ThemeSwitcher />
        </div>

        {/* Quote - Bottom Right (hidden on mobile) */}
        {/* <div className="absolute right-10 bottom-10 hidden md:block">
          <p className="max-w-xl text-right text-white/60">
            <span className="font-medium">"</span>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc eget augue nec massa volutpat aliquet.
            <span className="font-medium">"</span>
          </p>
        </div> */}

        {/* Login Card */}
        <div className="rounded-large bg-content1 shadow-small flex w-full max-w-sm flex-col gap-4 px-8 pt-6 pb-10 min-h-[400px] border border-divider">
          <p className="pb-2 text-xl font-medium text-foreground">Log In</p>
          
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {/* Email Input */}
            <Input
              isRequired
              label="Email Address"
              placeholder="Enter your email"
              type="email"
              value={email}
              onValueChange={setEmail}
              variant="bordered"
              size="lg"
              classNames={{
                inputWrapper: "shadow-xs border-medium border-default-200 data-[hover=true]:border-default-400 h-14",
                label: "text-foreground-500",
                input: "text-small"
              }}
            />

            {/* Password Input */}
            <Input
              isRequired
              label="Password"
              placeholder="Enter your password"
              type={isPasswordVisible ? "text" : "password"}
              value={password}
              onValueChange={setPassword}
              variant="bordered"
              size="lg"
              classNames={{
                inputWrapper: "shadow-xs border-medium border-default-200 data-[hover=true]:border-default-400 h-14",
                label: "text-foreground-500",
                input: "text-small"
              }}
              endContent={
                <button 
                  type="button" 
                  onClick={togglePasswordVisibility}
                  className="focus:outline-none"
                  aria-label="toggle password visibility"
                >
                  <Icon 
                    icon={isPasswordVisible ? "solar:eye-bold" : "solar:eye-closed-linear"}
                    className="text-default-400 pointer-events-none text-2xl"
                  />
                </button>
              }
            />

            {/* Cloudflare Turnstile CAPTCHA */}
            {isCaptchaEnabled && (
              <>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={'0x4AAAAAAB_UUskILjpwedbC'}
                  onSuccess={(token) => {
                    console.log('✅ CAPTCHA verified, token length:', token?.length);
                    captchaRef.current = token;
                    setCaptchaToken(token);
                  }}
                  onError={(error) => {
                    console.error('❌ CAPTCHA error:', error);
                    if (!captchaRef.current) {
                      setCaptchaToken('');
                    }
                  }}
                  onExpire={() => {
                    console.log('⏰ CAPTCHA expired');
                    captchaRef.current = '';
                    setCaptchaToken('');
                  }}
                  options={{
                    theme: resolvedTheme === 'dark' ? 'dark' : 'light',
                    size: 'normal',
                    refreshExpired: 'auto',
                  }}
                />
                
                {/* Helper text */}
                {!captchaToken && (
                  <p className="text-xs text-default-400 text-center">
                    Please complete the verification above to continue
                  </p>
                )}
              </>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              color="primary"
              size="md"
              className="w-full"
              isLoading={isLoading}
              isDisabled={!isFormValid || isLoading}
            >
              {/* {captchaToken ? 'Log In' : 'Complete CAPTCHA to Login'} */}
              {t('login')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
